import { useCallback, useEffect, useState } from 'react';
import {
  TreeView,
  Typography,
  Box,
  Checkbox,
  styled,
  TooltipProps,
  Tooltip,
  tooltipClasses,
  TreeItem,
} from '@bymason-private/legacy-pattern-components';
import { ScopeModel } from '@bymason/controller-api-hooks';
import { leafIds, flattenScopeParents } from './logic';

interface ScopeTreeProps {
  scopes: ScopeModel[] | void;
  userScopes?: string[];
  setChecked: React.Dispatch<React.SetStateAction<string[]>>;
  checked: string[];
}

export function ScopeTree({ scopes, checked, setChecked }: ScopeTreeProps) {
  const [parentFlatMap, setParentFlatMap] = useState<string[]>([]);

  const handleCheck = useCallback(
    (node: ScopeModel, newValue: boolean) => {
      if (!node.enabled || !node.active) {
        return;
      }
      const value = checked.includes(node.id);
      if (value === newValue) return;
      const leaves = leafIds(node);
      setChecked((old) =>
        newValue
          ? [...old.filter((s) => !leaves.includes(s)), node.id]
          : old.filter((id) => id !== node.id).filter((s) => !leaves.includes(s))
      );
    },
    [checked, setChecked]
  );

  const renderTree = useCallback(
    (scope: ScopeModel[], parentChecked = '') => {
      return scope.map((s) => {
        const isChecked = checked.includes(s.id);
        if (!s.active) {
          return;
        }
        return (
          <TreeItem
            key={s.id}
            nodeId={s.id}
            onClick={(e) => {
              handleCheck(s, !isChecked);
              e.stopPropagation();
            }}
            sx={{
              ['& .MuiTreeItem-iconContainer']: {
                '& .close': {
                  opacity: 0.3,
                },
                display: 'none !important',
                fontWeight: '600',
              },
              ['& .MuiTreeItem-group']: {
                ml: 1.5,
                pl: 3,
                borderLeft: '1px solid grey',
              },
              ['& .MuiTreeItem-label']: {
                fontWeight: 'inherit',
                color: 'inherit',
              },
            }}
            label={
              <HtmlTooltip
                followCursor
                title={
                  <>
                    <Typography fontWeight='bold' color='inherit'>
                      {s.id}
                    </Typography>
                    <Typography color='inherit'>{s.desc}</Typography>
                    {parentChecked !== '' && s.enabled && (
                      <Typography color='GrayText' fontSize='small'>
                        Uncheck <b>${parentChecked}</b> in order to edit this value
                      </Typography>
                    )}
                    {!s.enabled && (
                      <Typography fontWeight='bold' color='error'>
                        DISABLED
                      </Typography>
                    )}
                  </>
                }
                placement='right'
              >
                <Box sx={{ my: 'auto', mx: 0, display: 'flex', ml: -2 }}>
                  <Checkbox
                    checked={(isChecked || parentChecked != '') && s.enabled}
                    disabled={parentChecked != ''}
                    onClick={(e) => {
                      handleCheck(s, !isChecked);
                      e.stopPropagation();
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, my: 'auto' }}>
                    <Typography color={s.enabled ? '' : 'gray'} fontWeight={s.subscope ? 'bold' : 'normal'}>
                      {s.id}
                    </Typography>
                  </Box>
                </Box>
              </HtmlTooltip>
            }
          >
            {s.subscope &&
              renderTree(s.subscope, parentChecked !== '' ? parentChecked : isChecked ? s.id : '')}
          </TreeItem>
        );
      });
    },
    [checked, handleCheck]
  );

  useEffect(() => {
    scopes && setParentFlatMap(flattenScopeParents(scopes));
  }, [scopes]);

  return (
    <TreeView id='scopeTree' aria-label='file system navigator' expanded={parentFlatMap} selected={[]}>
      {scopes ? renderTree(scopes) : <div />}
    </TreeView>
  );
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));
